import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { withCapacitor } from 'Util/Capacitors/hoc';
import { SvgIcon } from 'Components/Base';
import { ClassroomInformation, ClassroomSubsection } from 'Domain/Classroom';
import {
  COMPONENT_TYPES,
  useGiveFeedback,
  useGetFeedback,
  useGetFeedbackTemplate,
} from 'Util/FeedBackService';
import { secondsToTime } from 'Util/datetime';
import { FeedbackReportModal } from 'Components/Feedback';
import { useClassroomInterruption, useRestorePlayerFocus } from 'Util/Hooks';
import useClassroomVideoPlayer from 'Util/Hooks/useClassroomVideoPlayer';

import { REPORT_STATE } from './constant';

const dislikeIconToggle = (
  <SvgIcon icon={SvgIcon.ICON_LIST.DISLIKE_OUTLINED} size={24} color={'white'} />
);

const DislikeFeedbackContainer = ({
  userId,
  meta,
  currentSubsectionId,
  contentType,
  toggleComponent: toggleComponentProp,
}) => {
  const [{ timeSnapshot, reportState }, setReportState] = useState({
    timeSnapshot: null,
    reportState: REPORT_STATE.INITIALIZED,
  });

  const {
    data: templateData,
    loading: isTemplateLoading,
    isSuccess: isGetTemplateSuccess,
  } = useGetFeedbackTemplate({
    templateId: 'DISLIKE_FEEDBACK',
  });

  const { data: feedbackData, loading: isGetFeedbackLoading } = useGetFeedback({
    templateId: 'LIKE_OR_DISLIKE',
    contentId: currentSubsectionId,
    userId,
  });
  // 0 --> no like, no dislike
  // 1 --> liked
  // -1 --> disliked
  const isDisliked = feedbackData?.getFeedback?.[0]?.data?.rating === -1;

  const { giveFeedback: dislike, loading: isDislikeLoading } = useGiveFeedback({
    templateId: 'LIKE_OR_DISLIKE',
  });

  const { giveFeedback, loading: isGiveFeedbackLoading } = useGiveFeedback({
    templateId: 'DISLIKE_FEEDBACK',
    onCompleted: () => setReportState({ timeSnapshot: null, reportState: REPORT_STATE.COMPLETED }),
  });

  const { activateInterruption, deactivateInterruption } = useClassroomInterruption();
  const { getCurrentTime } = useClassroomVideoPlayer();
  const restorePlayerFocus = useRestorePlayerFocus();

  if (!isGetTemplateSuccess) return null;

  const snapshotVideoTime = () => {
    activateInterruption();
    const currentTimeSecond = getCurrentTime();

    const timeSnapshot = secondsToTime(currentTimeSecond);
    setReportState({ timeSnapshot, reportState: REPORT_STATE.FILLING_FORM });
  };

  const handleCloseModal = () => {
    if (reportState === REPORT_STATE.COMPLETED) {
      setReportState({ timeSnapshot: null, reportState: REPORT_STATE.INITIALIZED });
    }
    deactivateInterruption();
    restorePlayerFocus();
  };

  let options = [];
  const inputComponents = templateData?.getTemplate?.components;

  if (Array.isArray(inputComponents) && inputComponents.length) {
    const selectionComponent = inputComponents.find(
      (component) => component.type === COMPONENT_TYPES.CHOICE
    );
    options =
      selectionComponent?.options?.map((option) => ({
        label: option.title,
        value: option.value,
        disabled: option.value === null ? true : false,
      })) ?? [];
  }

  if (isDisliked && reportState === REPORT_STATE.INITIALIZED) {
    return (
      <SvgIcon
        icon={SvgIcon.ICON_LIST.DISLIKE}
        size={24}
        color="white"
        onClick={() => {
          dislike(
            currentSubsectionId,
            userId,
            { ...meta, timeSnapshot },
            contentType,
            {
              rating: 0,
            },
            { shouldUpdate: true }
          );
          restorePlayerFocus();
        }}
      />
    );
  }

  const toggleComponent = toggleComponentProp ? toggleComponentProp : dislikeIconToggle;

  return (
    <FeedbackReportModal
      toggleComponent={toggleComponent}
      onOpenModal={snapshotVideoTime}
      onCloseModal={handleCloseModal}
      options={options}
      loading={
        isTemplateLoading || isGiveFeedbackLoading || isGetFeedbackLoading || isDislikeLoading
      }
      isSuccess={reportState === REPORT_STATE.COMPLETED}
      onSubmit={(formValue) => {
        dislike(
          currentSubsectionId,
          userId,
          { ...meta, timeSnapshot },
          contentType,
          {
            rating: -1,
          },
          { shouldUpdate: true }
        );
        giveFeedback(
          currentSubsectionId,
          userId,
          { ...meta, timeSnapshot },
          contentType,
          formValue
        );
      }}
    />
  );
};

const FeedbackDisLike = ({ capacitorContext: { deviceInfo }, toggleComponent }) => {
  return (
    <ClassroomInformation>
      {({ courseCode }) => (
        <ClassroomSubsection>
          {({
            title,
            contentType,
            classroomCourseId,
            currentSubsectionId,
            courseId,
            userProfileData,
          }) => {
            const meta = {
              classroomCourseId,
              courseId,
              courseCode,
              title,
              ...deviceInfo,
            };
            const userId = userProfileData?.userId;
            return (
              <DislikeFeedbackContainer
                userId={userId}
                meta={meta}
                currentSubsectionId={currentSubsectionId}
                contentType={contentType}
                toggleComponent={toggleComponent}
              />
            );
          }}
        </ClassroomSubsection>
      )}
    </ClassroomInformation>
  );
};

FeedbackDisLike.propTypes = {
  toggleComponent: PropTypes.element,
};

DislikeFeedbackContainer.propTypes = {
  toggleComponent: PropTypes.element,
};

export default withCapacitor(FeedbackDisLike);
